import * as Sentry from '@sentry/react';
import { ComponentProps, useCallback, useLayoutEffect, useState } from 'react';
import { Location, useLocation, useSearchParams } from 'react-router';
import { BillPayFlow } from './bill-pay-flow/components/bill-pay-flows-wrapper';
import { ExternalPaymentForm } from './external-payment-flow/components/external-payment-form';
import {
  beneficiaryIdStore,
  billPayFlowStore,
  DocumentDetails,
  documentDetailsStore,
  EvidencePrefill,
  evidencePrefillStore,
  PAYMENT_ACTION_PARAM_KEY,
  paymentActionStore,
  PaymentDetails,
  paymentDetailsStore,
  paymentsFlowEntryPointStore,
  shouldRenderReviewStepStore,
  uploadedEvidenceIdStore,
} from './payment-flow-storage';

const PAYMENT_AUTH_STATUS_PARAM_KEY = 'status';
const PAYMENT_ID_PARAM_KEY = 'paymentId';
const TRANSACTION_ID_PARAM_KEY = 'transactionId';

export type PaymentAction =
  | 'INTERNAL_PAYMENT'
  | 'EXTERNAL_PAYMENT'
  | 'VIEW_ALL_BENEFICIARIES'
  | 'CREATE_BENEFICIARY'
  | 'BILL_PAY'
  | 'BILL_PAY_UPLOAD_INVOICE';

export type PaymentsFlowEntryPoint =
  | Extract<PaymentAction, 'BILL_PAY_UPLOAD_INVOICE' | 'VIEW_ALL_BENEFICIARIES'>
  | 'INVOICE_PAGE'
  | 'TREASURY_PAGE_DEPOSIT'
  | 'TREASURY_PAGE_WITHDRAWAL';

type LocationState = {
  isFromManualPayInvoicePage?: boolean;
  isFromToPayRowReviewInvoicePageConfig?:
    | {
        isFromToPayRowReviewInvoicePage: false;
      }
    | {
        isFromToPayRowReviewInvoicePage: true;
        invoiceId: string;
        invoiceReference?: string;
        fileName?: string;
        documentUrl?: string;
      };
  isFromDepositTreasuryPageConfig?:
    | {
        isFromDepositTreasuryPage: false;
      }
    | {
        isFromDepositTreasuryPage: true;
        investmentAccountId: string;
      };
  isFromWithdrawalTreasuryPageConfig?:
    | {
        isFromWithdrawalTreasuryPage: false;
      }
    | {
        isFromWithdrawalTreasuryPage: true;
        investmentAccountId: string;
      };
};

// Determined in the server
type PaymentAuthStatus = 'success' | 'error';

export const usePaymentFlow = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasSearchParams = searchParams.size > 0;
  // useLocation doesn't accept a generic type for the state
  // https://github.com/reach/router/issues/414#issuecomment-1753079222
  const { state }: Location<LocationState> | undefined = useLocation();

  const initialPaymentAction = !hasSearchParams ? null : paymentActionStore.get();
  const [paymentAction, setPaymentAction] = useState<PaymentAction | null>(initialPaymentAction);

  const [billPayFlow, setBillPayFlow] = useState<BillPayFlow | null>(() => billPayFlowStore.get());
  const [shouldRenderReviewStep, setShouldRenderReviewStep] = useState<boolean>(() =>
    shouldRenderReviewStepStore.get(),
  );

  const [externalPaymentFormValues, setExternalPaymentFormValues] = useState<
    Parameters<ComponentProps<typeof ExternalPaymentForm>['onSubmit']>[0] | undefined
  >(undefined);

  // Contrary to other states, paymentDetails  is not initialised with the value from the store to avoid closure issues in the external payment flow
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [documentDetails, setDocumentDetails] = useState<DocumentDetails | null>(() => documentDetailsStore.get());
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState<string | null>(() => beneficiaryIdStore.get());
  const [uploadedEvidenceId, setUploadedEvidenceId] = useState<string | null>(() => uploadedEvidenceIdStore.get());
  const [evidencePrefill, setEvidencePrefill] = useState<EvidencePrefill | null>(() => null);
  const [paymentsFlowEntryPoint, setPaymentsFlowEntryPoint] = useState<PaymentsFlowEntryPoint | null>(null);
  const [shouldSkipLoadingStep, setShouldSkipLoadingStep] = useState<boolean>(false);

  const updatePaymentAction = (action: PaymentAction) => {
    setPaymentAction(action);

    searchParams.set(PAYMENT_ACTION_PARAM_KEY, action);
    setSearchParams(searchParams);

    paymentActionStore.update(action);
  };

  const updateBillPayFlow = (flow: BillPayFlow) => {
    setBillPayFlow(flow);
    billPayFlowStore.update(flow);
  };

  const getShouldRenderReviewStep = (): boolean => {
    if (!shouldRenderReviewStep) {
      return shouldRenderReviewStepStore.get();
    }

    return shouldRenderReviewStep;
  };

  const updateShouldRenderReviewStep = (value: boolean) => {
    setShouldRenderReviewStep(value);
    shouldRenderReviewStepStore.update(value);
  };

  const clearShouldRenderReviewStep = () => {
    setShouldRenderReviewStep(false);
    shouldRenderReviewStepStore.clear();
  };

  const getPaymentDetails = (): PaymentDetails | null => {
    if (!paymentDetails) {
      return paymentDetailsStore.get();
    }

    return paymentDetails;
  };

  const updatePaymentDetails = (details: PaymentDetails) => {
    setPaymentDetails(details);
    paymentDetailsStore.update(details);
  };

  const clearPaymentDetails = () => {
    setPaymentDetails(null);
    paymentDetailsStore.clear();
  };

  const getDocumentDetails = (): DocumentDetails | null => {
    if (!documentDetails) {
      return documentDetailsStore.get();
    }

    return documentDetails;
  };

  const updateDocumentDetails = (details: DocumentDetails) => {
    setDocumentDetails(details);
    documentDetailsStore.update(details);
  };

  const clearDocumentDetails = () => {
    setDocumentDetails(null);
    documentDetailsStore.clear();
  };

  const getSelectedBeneficiaryId = (): string | null => {
    if (!selectedBeneficiaryId) {
      return beneficiaryIdStore.get();
    }

    return selectedBeneficiaryId;
  };

  const updateSelectedBeneficiaryId = (beneficiaryId: string) => {
    setSelectedBeneficiaryId(beneficiaryId);
    beneficiaryIdStore.update(beneficiaryId);
  };

  const clearSelectedBeneficiaryId = () => {
    setSelectedBeneficiaryId(null);
    beneficiaryIdStore.clear();
  };

  const getUploadedEvidenceId = () => {
    if (!uploadedEvidenceId) {
      return uploadedEvidenceIdStore.get();
    }

    return uploadedEvidenceId;
  };

  const updateUploadedEvidenceId = (evidenceId: string) => {
    setUploadedEvidenceId(evidenceId);
    uploadedEvidenceIdStore.update(evidenceId);
  };

  const clearUploadedEvidenceId = () => {
    setUploadedEvidenceId(null);
    uploadedEvidenceIdStore.clear();
  };

  const clearPaymentAction = () => {
    setPaymentAction(null);

    searchParams.delete(PAYMENT_ACTION_PARAM_KEY);
    setSearchParams(searchParams);

    paymentActionStore.clear();
  };

  const clearBillPayFlow = () => {
    setBillPayFlow(null);
    billPayFlowStore.clear();
  };

  const clearAuthParams = () => {
    searchParams.delete(PAYMENT_AUTH_STATUS_PARAM_KEY);
    searchParams.delete(PAYMENT_ID_PARAM_KEY);
    searchParams.delete(TRANSACTION_ID_PARAM_KEY);

    setSearchParams(searchParams);
  };

  const getEvidencePrefill = (): EvidencePrefill | null => {
    if (!evidencePrefill) {
      return evidencePrefillStore.get();
    }

    return evidencePrefill;
  };

  const updateEvidencePrefill = (data: EvidencePrefill) => {
    setEvidencePrefill(data);
    evidencePrefillStore.update(data);
  };

  const clearEvidencePrefill = () => {
    setEvidencePrefill(null);
    evidencePrefillStore.clear();
  };

  const getPaymentsFlowEntryPoint = (): PaymentsFlowEntryPoint | null => {
    if (!paymentsFlowEntryPoint) {
      return paymentsFlowEntryPointStore.get();
    }

    return paymentsFlowEntryPoint;
  };

  const updatePaymentsFlowEntryPoint = (entryPoint: PaymentsFlowEntryPoint) => {
    setPaymentsFlowEntryPoint(entryPoint);
    paymentsFlowEntryPointStore.update(entryPoint);
  };

  const clearPaymentsFlowEntryPoint = () => {
    setPaymentsFlowEntryPoint(null);
    paymentsFlowEntryPointStore.clear();
  };

  const clearBillPayState = () => {
    clearBillPayFlow();
    clearUploadedEvidenceId();
    clearPaymentDetails();
    clearDocumentDetails();
    clearShouldRenderReviewStep();
    clearSelectedBeneficiaryId();
    clearEvidencePrefill();
    clearPaymentsFlowEntryPoint();
    setShouldSkipLoadingStep(false);
  };

  const clearPaymentState = () => {
    clearPaymentAction();
    clearBillPayState();
    clearAuthParams();
  };

  const cachedUpdatePaymentAction = useCallback(updatePaymentAction, [searchParams, setSearchParams]);
  const cachedClearBillPayState = useCallback(clearBillPayState, []);

  // Prevents visual glitch when user navigates from invoice page to payment flow
  // React says it can hurt performance. When it does for us, we can review the implementation
  // https://react.dev/reference/react/useLayoutEffect
  useLayoutEffect(() => {
    if (state?.isFromToPayRowReviewInvoicePageConfig?.isFromToPayRowReviewInvoicePage) {
      return;
    }

    if (state?.isFromManualPayInvoicePage) {
      Sentry.addBreadcrumb({
        message: 'Landed in bill pay flow from manual pay button in invoice page',
      });

      cachedClearBillPayState();

      cachedUpdatePaymentAction('BILL_PAY_UPLOAD_INVOICE');

      updatePaymentsFlowEntryPoint('INVOICE_PAGE');
    }
  }, [
    cachedClearBillPayState,
    cachedUpdatePaymentAction,
    state?.isFromManualPayInvoicePage,
    state?.isFromToPayRowReviewInvoicePageConfig?.isFromToPayRowReviewInvoicePage,
  ]);

  useLayoutEffect(() => {
    if (state?.isFromManualPayInvoicePage) {
      return;
    }

    if (
      state?.isFromToPayRowReviewInvoicePageConfig?.isFromToPayRowReviewInvoicePage &&
      state?.isFromToPayRowReviewInvoicePageConfig.fileName &&
      state?.isFromToPayRowReviewInvoicePageConfig.documentUrl
    ) {
      Sentry.addBreadcrumb({
        message: 'Landed in bill pay flow from unpaid invoices table review button in invoice page',
      });

      cachedClearBillPayState();

      cachedUpdatePaymentAction('BILL_PAY');

      updateUploadedEvidenceId(state?.isFromToPayRowReviewInvoicePageConfig.invoiceId);

      updateEvidencePrefill({
        reference: state?.isFromToPayRowReviewInvoicePageConfig.invoiceReference ?? undefined,
      });

      updateDocumentDetails({
        fileName: state?.isFromToPayRowReviewInvoicePageConfig.fileName,
        documentUrl: state?.isFromToPayRowReviewInvoicePageConfig.documentUrl,
      });

      setShouldSkipLoadingStep(true);
      updatePaymentsFlowEntryPoint('INVOICE_PAGE');
    }
  }, [cachedUpdatePaymentAction, state, cachedClearBillPayState]);

  useLayoutEffect(() => {
    if (state?.isFromDepositTreasuryPageConfig?.isFromDepositTreasuryPage) {
      Sentry.addBreadcrumb({
        message: 'Landed in internal pay flow from deposit button in treasury page',
      });

      cachedClearBillPayState();

      cachedUpdatePaymentAction('INTERNAL_PAYMENT');
      updatePaymentsFlowEntryPoint('TREASURY_PAGE_DEPOSIT');
      updatePaymentDetails({
        // Forced to put a '' as a placeholder for now because sourceAccountId is required in PaymentDetails, even though we don't need it for deposit
        sourceAccountId: '',
        destinationAccountId: state?.isFromDepositTreasuryPageConfig?.investmentAccountId,
        // Forced to put a 0 as a placeholder for now because amount is required in PaymentDetails, even though we don't need it for deposit
        amount: 0,
      });
    }
  }, [cachedClearBillPayState, cachedUpdatePaymentAction, state?.isFromDepositTreasuryPageConfig]);

  useLayoutEffect(() => {
    if (state?.isFromWithdrawalTreasuryPageConfig?.isFromWithdrawalTreasuryPage) {
      Sentry.addBreadcrumb({
        message: 'Landed in internal pay flow from withdrawal button in treasury page',
      });

      cachedClearBillPayState();

      cachedUpdatePaymentAction('INTERNAL_PAYMENT');
      updatePaymentsFlowEntryPoint('TREASURY_PAGE_WITHDRAWAL');
      updatePaymentDetails({
        sourceAccountId: state?.isFromWithdrawalTreasuryPageConfig?.investmentAccountId,
        // Forced to put a 0 as a placeholder for now because amount is required in PaymentDetails, even though we don't need it for withdrawal
        amount: 0,
      });
    }
  }, [cachedClearBillPayState, cachedUpdatePaymentAction, state?.isFromWithdrawalTreasuryPageConfig]);

  return {
    paymentAction,
    authorisationStatus: searchParams.get(PAYMENT_AUTH_STATUS_PARAM_KEY) as PaymentAuthStatus | null,
    paymentId: searchParams.get(PAYMENT_ID_PARAM_KEY),
    transactionId: searchParams.get(TRANSACTION_ID_PARAM_KEY),
    updatePaymentAction,
    clearPaymentAction,
    billPayFlow,
    updateBillPayFlow,
    shouldRenderReviewStep,
    getShouldRenderReviewStep,
    updateShouldRenderReviewStep,
    clearShouldRenderReviewStep,
    updatePaymentDetails,
    getPaymentDetails,
    clearPaymentDetails,
    clearAuthParams,
    selectedBeneficiaryId,
    getSelectedBeneficiaryId,
    updateSelectedBeneficiaryId,
    clearSelectedBeneficiaryId,
    getDocumentDetails,
    updateDocumentDetails,
    uploadedEvidenceId,
    getUploadedEvidenceId,
    updateUploadedEvidenceId,
    getEvidencePrefill,
    updateEvidencePrefill,
    getPaymentsFlowEntryPoint,
    updatePaymentsFlowEntryPoint,
    clearBillPayState,
    clearPaymentState,
    shouldSkipLoadingStep,
    formValuesData: {
      externalPayment: {
        values: externalPaymentFormValues,
        setValues: setExternalPaymentFormValues,
      },
    },
  };
};
